import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FiUser, FiMail, FiPhone, FiLock, FiEye, FiEyeOff,
    FiDollarSign, FiMapPin, FiCalendar, FiBriefcase,
    FiSave, FiX, FiRefreshCw, FiCheckCircle, FiXCircle
} from 'react-icons/fi';
import { API_URL } from '../../../api/api';

const API_EMPLOYEE = `${API_URL}/employees`;
const API_EMPLOYEE_PROFILE = `${API_URL}/employee-profiles`;
const API_BRANCH = `${API_URL}/branches`;
const API_PERMISSION = `${API_URL}/permissions`;

const AddEmployeeLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const [form, setForm] = useState({
        employeeprofile: '',
        designation: '',
        firstname: '',
        lastname: '',
        email: '',
        backdate: '',
        permission: '',
        activeuser: '1',
        loginusername: '',
        password: '',
        mobileno: '',
        salary: '',
        branch_id: '',
        image: 'https://ranjanfinance.com/software/assets/defalt.png',
    });

    const [profiles, setProfiles] = useState([]);
    const [branches, setBranches] = useState([]);
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        fetchProfiles();
        fetchBranches();
        fetchPermissions();
        if (id) {
            fetchEmployee(id);
        }
    }, [id]);

    const fetchProfiles = async () => {
        try {
            const res = await axios.get(API_EMPLOYEE_PROFILE);
            setProfiles(res.data || []);
        } catch (error) {
            console.error('Error fetching profiles:', error);
        }
    };

    const fetchBranches = async () => {
        try {
            const res = await axios.get(API_BRANCH);
            setBranches(res.data || []);
        } catch (error) {
            console.error('Error fetching branches:', error);
        }
    };

    const fetchPermissions = async () => {
        try {
            const res = await axios.get(API_PERMISSION);
            if (Array.isArray(res.data.permissions)) {
                setPermissions(res.data.permissions);
            } else if (Array.isArray(res.data)) {
                setPermissions(res.data);
            } else {
                setPermissions([]);
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const fetchEmployee = async (employeeId) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_EMPLOYEE}/${employeeId}`);
            const emp = res.data;
            setForm({
                employeeprofile: emp.employeeprofile || '',
                designation: emp.designation || '',
                firstname: emp.firstname || '',
                lastname: emp.lastname || '',
                email: emp.email || '',
                backdate: emp.backdate || '',
                permission: emp.permission || '',
                activeuser: emp.activeuser ? '1' : '0',
                loginusername: emp.loginusername || '',
                password: '',
                mobileno: emp.mobileno || '',
                salary: emp.salary || '',
                branch_id: emp.branch_id || '',
                image: emp.image || 'https://ranjanfinance.com/software/assets/defalt.png',
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to load employee data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setForm({
            employeeprofile: '',
            designation: '',
            firstname: '',
            lastname: '',
            email: '',
            backdate: '',
            permission: '',
            activeuser: '1',
            loginusername: '',
            password: '',
            mobileno: '',
            salary: '',
            branch_id: '',
            image: 'https://ranjanfinance.com/software/assets/defalt.png',
        });
        setErrors({});
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.employeeprofile) newErrors.employeeprofile = 'Employee profile is required';
        if (!form.designation) newErrors.designation = 'Designation is required';
        if (!form.firstname) newErrors.firstname = 'First name is required';
        if (!form.lastname) newErrors.lastname = 'Last name is required';

        if (!form.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(form.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!form.loginusername) newErrors.loginusername = 'Login username is required';
        if (!id && !form.password) newErrors.password = 'Password is required';

        if (!form.mobileno) {
            newErrors.mobileno = 'Mobile number is required';
        } else if (!/^\d{10,15}$/.test(form.mobileno)) {
            newErrors.mobileno = 'Mobile number is invalid (10-15 digits)';
        }

        if (!form.branch_id) newErrors.branch_id = 'Branch is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = e => {
        const { name, value, type } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'radio' ? value : value,
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill all required fields correctly.',
            });
            return;
        }

        setLoading(true);
        try {
            const payload = { ...form };
            if (id && !form.password) {
                delete payload.password;
            }

            if (id) {
                await axios.put(`${API_EMPLOYEE}/${id}`, payload);
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'Employee updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                await axios.post(API_EMPLOYEE, payload);
                Swal.fire({
                    icon: 'success',
                    title: 'Created!',
                    text: 'Employee created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            }

            setTimeout(() => {
                navigate('/manage-employee');
            }, 2000);
        } catch (error) {
            console.error('Error saving employee:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Something went wrong',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/users');
    };

    if (loading && id) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading employee data...</p>
            </div>
        );
    }

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-gradient-primary text-white py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="mb-0">
                                            <FiUser className="me-2" />
                                            {id ? 'Update Employee' : 'Add New Employee'}
                                        </h5>
                                        <p className="mb-0 opacity-75 small">
                                            {id ? 'Edit employee information' : 'Create a new employee account'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleCancel}
                                        className="btn btn-light btn-sm"
                                    >
                                        <FiX className="me-1" /> Cancel
                                    </button>
                                </div>
                            </div>

                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-4">
                                        {/* Left Column */}
                                        <div className="col-lg-6">
                                            <div className="card h-100 border-0 shadow-sm">
                                                <div className="card-header bg-light py-3">
                                                    <h6 className="mb-0">
                                                        <FiBriefcase className="me-2" />
                                                        Professional Information
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiUser className="me-1" />
                                                            Employee Profile *
                                                        </label>
                                                        <select
                                                            name="employeeprofile"
                                                            className={`form-select ${errors.employeeprofile ? 'is-invalid' : ''}`}
                                                            value={form.employeeprofile}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Select Employee Profile</option>
                                                            {profiles.map(p => (
                                                                <option key={p.id} value={p.profile_name}>
                                                                    {p.profile_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors.employeeprofile && (
                                                            <div className="invalid-feedback">{errors.employeeprofile}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiBriefcase className="me-1" />
                                                            Designation *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="designation"
                                                            className={`form-control ${errors.designation ? 'is-invalid' : ''}`}
                                                            value={form.designation}
                                                            onChange={handleChange}
                                                            placeholder="e.g., Software Engineer"
                                                        />
                                                        {errors.designation && (
                                                            <div className="invalid-feedback">{errors.designation}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiUser className="me-1" />
                                                            First Name *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="firstname"
                                                            className={`form-control ${errors.firstname ? 'is-invalid' : ''}`}
                                                            value={form.firstname}
                                                            onChange={handleChange}
                                                            placeholder="Enter first name"
                                                        />
                                                        {errors.firstname && (
                                                            <div className="invalid-feedback">{errors.firstname}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiMail className="me-1" />
                                                            Email Address *
                                                        </label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                            value={form.email}
                                                            onChange={handleChange}
                                                            placeholder="employee@company.com"
                                                        />
                                                        {errors.email && (
                                                            <div className="invalid-feedback">{errors.email}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiCalendar className="me-1" />
                                                            Back Date (Days)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="backdate"
                                                            className="form-control"
                                                            value={form.backdate}
                                                            onChange={handleChange}
                                                            placeholder="0"
                                                            min="0"
                                                        />
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiCheckCircle className="me-1" />
                                                            Permission
                                                        </label>
                                                        <select
                                                            name="permission"
                                                            className="form-select"
                                                            value={form.permission}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Select Permission</option>
                                                            {permissions.map(p => (
                                                                <option key={p.id} value={p.name}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiCheckCircle className="me-1" />
                                                            User Active
                                                        </label>
                                                        <div className="d-flex gap-4 mt-2">
                                                            <div className="form-check">
                                                                <input
                                                                    type="radio"
                                                                    name="activeuser"
                                                                    value="1"
                                                                    id="active-yes"
                                                                    className="form-check-input"
                                                                    checked={form.activeuser === '1'}
                                                                    onChange={handleChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="active-yes">
                                                                    <FiCheckCircle className="me-1 text-success" />
                                                                    Active
                                                                </label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input
                                                                    type="radio"
                                                                    name="activeuser"
                                                                    value="0"
                                                                    id="active-no"
                                                                    className="form-check-input"
                                                                    checked={form.activeuser === '0'}
                                                                    onChange={handleChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="active-no">
                                                                    <FiXCircle className="me-1 text-danger" />
                                                                    Inactive
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column */}
                                        <div className="col-lg-6">
                                            <div className="card h-100 border-0 shadow-sm">
                                                <div className="card-header bg-light py-3">
                                                    <h6 className="mb-0">
                                                        <FiUser className="me-2" />
                                                        Account & Personal Information
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiUser className="me-1" />
                                                            Login Username *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="loginusername"
                                                            className={`form-control ${errors.loginusername ? 'is-invalid' : ''}`}
                                                            value={form.loginusername}
                                                            onChange={handleChange}
                                                            placeholder="Enter login username"
                                                        />
                                                        {errors.loginusername && (
                                                            <div className="invalid-feedback">{errors.loginusername}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiLock className="me-1" />
                                                            Password {!id && '*'}
                                                        </label>
                                                        <div className="input-group">
                                                            <input
                                                                type={showPassword ? 'text' : 'password'}
                                                                name="password"
                                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                                value={form.password}
                                                                onChange={handleChange}
                                                                required={!id}
                                                                placeholder={id ? "Leave blank to keep current" : "Enter password"}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={() => setShowPassword(!showPassword)}
                                                            >
                                                                {showPassword ? <FiEyeOff /> : <FiEye />}
                                                            </button>
                                                        </div>
                                                        {errors.password && (
                                                            <div className="invalid-feedback d-block">{errors.password}</div>
                                                        )}
                                                        {id && (
                                                            <small className="text-muted">Leave password blank to keep current password</small>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiUser className="me-1" />
                                                            Last Name *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="lastname"
                                                            className={`form-control ${errors.lastname ? 'is-invalid' : ''}`}
                                                            value={form.lastname}
                                                            onChange={handleChange}
                                                            placeholder="Enter last name"
                                                        />
                                                        {errors.lastname && (
                                                            <div className="invalid-feedback">{errors.lastname}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiPhone className="me-1" />
                                                            Mobile Number *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="mobileno"
                                                            className={`form-control ${errors.mobileno ? 'is-invalid' : ''}`}
                                                            value={form.mobileno}
                                                            onChange={handleChange}
                                                            placeholder="10-15 digits"
                                                        />
                                                        {errors.mobileno && (
                                                            <div className="invalid-feedback">{errors.mobileno}</div>
                                                        )}
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiDollarSign className="me-1" />
                                                            Salary
                                                        </label>
                                                        <div className="input-group">
                                                            <span className="input-group-text">₹</span>
                                                            <input
                                                                type="number"
                                                                name="salary"
                                                                className="form-control"
                                                                value={form.salary}
                                                                onChange={handleChange}
                                                                placeholder="0.00"
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label fw-semibold">
                                                            <FiMapPin className="me-1" />
                                                            Branch *
                                                        </label>
                                                        <select
                                                            name="branch_id"
                                                            className={`form-select ${errors.branch_id ? 'is-invalid' : ''}`}
                                                            value={form.branch_id}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Select Branch</option>
                                                            {branches.map(b => (
                                                                <option key={b.id} value={b.id}>
                                                                    {b.branchname}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors.branch_id && (
                                                            <div className="invalid-feedback">{errors.branch_id}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="row mt-4">
                                        <div className="col-12">
                                            <div className="d-flex justify-content-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleReset}
                                                    className="btn btn-outline-secondary px-4"
                                                    disabled={loading}
                                                >
                                                    <FiRefreshCw className="me-2" />
                                                    Reset
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleCancel}
                                                    className="btn btn-outline-danger px-4"
                                                    disabled={loading}
                                                >
                                                    <FiX className="me-2" />
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {id ? 'Updating...' : 'Creating...'}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiSave className="me-2" />
                                                            {id ? 'Update Employee' : 'Add Employee'}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom CSS */}
            <style jsx="true">{`
                .card {
                    border-radius: 12px;
                    overflow: hidden;
                    transition: all 0.3s ease;
                }

                .card:hover {
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }

                .card-header {
                    border-bottom: 1px solid rgba(0,0,0,.125);
                }

                .bg-gradient-primary {
                    background: linear-gradient(135deg, #4a90e2, #2c6bb5) !important;
                }

                .form-control:focus, .form-select:focus {
                    border-color: #4a90e2;
                    box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
                }

                .invalid-feedback {
                    font-size: 0.875em;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #4a90e2, #2c6bb5);
                    border: none;
                    transition: all 0.3s ease;
                }

                .btn-primary:hover {
                    background: linear-gradient(135deg, #2c6bb5, #1a4a8f);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
                }

                .btn-outline-secondary:hover, .btn-outline-danger:hover {
                    transform: translateY(-2px);
                }

                /* Custom Radio Button Styling */
                .form-check-input:checked {
                    background-color: #4a90e2;
                    border-color: #4a90e2;
                }

                .form-check-input:focus {
                    border-color: #4a90e2;
                    box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
                }

                .form-check-label {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    user-select: none;
                }

                /* Enhanced Input Group */
                .input-group .form-control {
                    border-right: 0;
                }

                .input-group .btn-outline-secondary {
                    border-left: 0;
                    border-color: #ced4da;
                }

                .input-group .btn-outline-secondary:hover {
                    background-color: #f8f9fa;
                }

                /* Required field indicator */
                .form-label[required]::after {
                    content: ' *';
                    color: #dc3545;
                }

                /* Smooth animations */
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .card {
                    animation: fadeIn 0.5s ease-out;
                }

                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .card-body {
                        padding: 1rem !important;
                    }
                    
                    .d-flex.gap-4 {
                        flex-direction: column;
                        gap: 0.5rem !important;
                    }
                }
            `}</style>
        </>
    );
};

export default AddEmployeeLayer;